import React from "react";
import { View, StyleSheet, Pressable, Linking, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { FileMetadata } from "@/types/files";
import { ModernCard } from "./ModernCard";

interface AttachmentsCardProps {
  attachments: FileMetadata[];
  style?: any;
}

export function AttachmentsCard({ attachments, style }: AttachmentsCardProps) {
  const { colors } = useTheme();

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const getFileIcon = (type: string): keyof typeof MaterialCommunityIcons.glyphMap => {
    if (type.startsWith("image/")) return "file-image";
    if (type.startsWith("video/")) return "file-video";
    if (type.startsWith("audio/")) return "file-music";
    if (type.includes("pdf")) return "file-pdf-box";
    if (type.includes("document") || type.includes("word")) return "file-document";
    if (type.includes("spreadsheet") || type.includes("excel")) return "file-excel";
    return "file-document-outline";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFilePress = async (file: FileMetadata) => {
    try {
      const supported = await Linking.canOpenURL(file.url);
      if (supported) {
        await Linking.openURL(file.url);
      } else {
        console.error("Cannot open file URL:", file.url);
      }
    } catch (error) {
      console.error("Failed to open file:", error);
    }
  };

  return (
    <ModernCard style={style}>
      <View style={styles.header}>
        <MaterialCommunityIcons name="paperclip" size={20} color={colors.text} />
        <ThemedText type="defaultSemiBold">Important Files</ThemedText>
        <View style={[styles.badge, { backgroundColor: colors.primary + "20" }]}>
          <ThemedText style={[styles.badgeText, { color: colors.primary }]}>{attachments.length}</ThemedText>
        </View>
      </View>

      <View style={styles.filesList}>
        {attachments.map((file) => (
          <Pressable
            key={file.id}
            style={({ pressed }) => [
              styles.fileItem,
              { borderColor: colors.border },
              pressed && Platform.OS !== "web" && { backgroundColor: colors.backgroundSecondary },
            ]}
            onPress={() => handleFilePress(file)}
          >
            <View style={[styles.iconContainer, { backgroundColor: colors.primary + "15" }]}>
              <MaterialCommunityIcons name={getFileIcon(file.type)} size={24} color={colors.primary} />
            </View>

            <View style={styles.fileInfo}>
              <ThemedText style={styles.fileName} numberOfLines={1}>
                {file.name}
              </ThemedText>
              <ThemedText style={[styles.fileSize, { color: colors.textSecondary }]}>
                {formatFileSize(file.size)}
              </ThemedText>
            </View>

            <MaterialCommunityIcons name="download" size={20} color={colors.textSecondary} />
          </Pressable>
        ))}
      </View>

      <View style={[styles.footer, { backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons name="information-outline" size={14} color={colors.textSecondary} />
        <ThemedText style={[styles.footerText, { color: colors.textSecondary }]}>
          Tap any file to view or download
        </ThemedText>
      </View>
    </ModernCard>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    marginLeft: "auto",
  },
  badgeText: {
    fontSize: 12,
    fontWeight: "600",
  },
  filesList: {
    gap: 8,
  },
  fileItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  fileInfo: {
    flex: 1,
    gap: 2,
  },
  fileName: {
    fontSize: 14,
    fontWeight: "500",
  },
  fileSize: {
    fontSize: 12,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    padding: 10,
    borderRadius: 8,
  },
  footerText: {
    fontSize: 12,
  },
});
